import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { MAQUININHAS_DB } from "@/lib/maquininhas-db";
import { trackEvent, startTimer, getElapsedTime } from "@/lib/analytics";
import { 
  Check, 
  X, 
  Info, 
  ExternalLink, 
  Calculator, 
  Smartphone, 
  Calendar, 
  HelpCircle, 
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  Map,
  Trophy,
  Activity
} from "lucide-react";

export const Route = createFileRoute("/ferramentas/comparador-maquininhas")({
  head: () => ({
    meta: [
      { title: "Comparador de Maquininhas MEI 2026: Taxas e Recursos" },
      {
        name: "description",
        content:
          "Compare maquininhas para MEI em 2026. Veja taxas, Pix, recebimento, Tap to Pay, VR/VA, aluguel, conta digital e escolha a melhor opção para o seu negócio.",
      },
      { property: "og:title", content: "Comparador de Maquininhas MEI 2026: Taxas e Recursos" },
      {
        property: "og:description",
        content:
          "Compare maquininhas para MEI em 2026. Veja taxas, Pix, recebimento, Tap to Pay, VR/VA, aluguel, conta digital e escolha a melhor opção para o seu negócio.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/ferramentas/comparador-maquininhas" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/ferramentas/comparador-maquininhas" }],
  }),
  component: ComparadorPage,
});

function ComparadorPage() {
  // Estado para selecionar as operadoras na visualização (por padrão todas selecionadas)
  const [selectedIds, setSelectedIds] = useState<string[]>(
    MAQUININHAS_DB.map((m) => m.id)
  );

  useEffect(() => {
    // Iniciar temporizadores de tomada de decisão e conversão
    startTimer("time_to_affiliate");
    startTimer("time_to_simulation");

    // Rastrear entrada e geração da página
    trackEvent("comparison_started");
    trackEvent("recommendation_generated");

    // Medição de profundidade de rolagem (comparison_depth_measured)
    const trackedDepths = new Set<number>();
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.min(100, Math.round((scrollTop / docHeight) * 100));

      const checkDepth = (depth: number) => {
        if (pct >= depth && !trackedDepths.has(depth)) {
          trackedDepths.add(depth);
          trackEvent("comparison_depth_measured", { scroll_percentage: depth });
        }
      };

      checkDepth(25);
      checkDepth(50);
      checkDepth(75);
      checkDepth(100);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleSelect = (id: string) => {
    const isSelected = selectedIds.includes(id);
    trackEvent("operator_toggled", {
      operator_id: id,
      action: isSelected ? "deselected" : "selected"
    });
    trackEvent("recommendation_changed");

    if (isSelected) {
      if (selectedIds.length > 2) { // Garantir pelo menos duas para comparação útil
        setSelectedIds(selectedIds.filter((item) => item !== id));
      }
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectedMaquinas = MAQUININHAS_DB.filter((m) => selectedIds.includes(m.id));

  // Formatação de valores percentuais
  const formatPct = (value: number) => {
    return value.toFixed(2).replace(".", ",").replace(",00", "") + "%";
  };

  // Formatação para BRL
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  };

  return (
    <section className="container mx-auto px-4 py-12 max-w-6xl space-y-12">
      {/* Cabeçalho */}
      <div className="text-center max-w-3xl mx-auto">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary mb-3">
          <TrendingUp className="h-3.5 w-3.5" /> Atualizado para 2026
        </span>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-display">
          Comparador de Maquininhas para MEI
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed text-sm md:text-base">
          Analise taxas, prazos de recebimento, custos de aquisição e recursos tecnológicos 
          de cada operadora lado a lado. Selecione as marcas abaixo para personalizar o seu comparativo.
        </p>
      </div>

      {/* Jornada do Usuário & Como Usar */}
      <div className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <Map className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-foreground font-display">Sua Jornada de Decisão: Como Usar</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 relative">
          <div className="space-y-2 relative">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                1
              </span>
              <h3 className="font-semibold text-foreground text-sm">Comparar Recursos</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Filtre as operadoras desejadas e analise na tabela quais aceitam vouchers, possuem Tap to Pay no celular ou oferecem comodato.
            </p>
          </div>
          <div className="space-y-2 relative">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                2
              </span>
              <h3 className="font-semibold text-foreground text-sm">Simular Matemática</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Use as chamadas contextuais de cada marca para ir ao Simulador. Digite seu faturamento e veja qual entrega o maior retorno líquido.
            </p>
          </div>
          <div className="space-y-2 relative">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                3
              </span>
              <h3 className="font-semibold text-foreground text-sm">Escolha Inteligente</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Tome uma decisão independente e isenta baseada em dados reais do seu faturamento mensal, sem pegadinhas comerciais.
            </p>
          </div>
        </div>
      </div>

      {/* Vencedores por Categoria */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border border-primary/20 p-6 md:p-8 space-y-4">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500 animate-pulse" />
          <h2 className="text-lg font-bold text-foreground font-display">Destaques por Categoria (Referência Geral)</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-card rounded-xl border border-border/80 p-4 space-y-1 hover:border-primary/30 transition shadow-xs">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Menor Débito</span>
            <strong className="text-foreground text-sm block">Stone</strong>
            <span className="text-[11px] font-bold text-success block">0,79%</span>
          </div>
          <div className="bg-card rounded-xl border border-border/80 p-4 space-y-1 hover:border-primary/30 transition shadow-xs">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Menor Parcelado 12x</span>
            <strong className="text-foreground text-sm block">Ton</strong>
            <span className="text-[11px] font-bold text-success block">3,94% <span className="text-[9px] text-muted-foreground">(PromoTon)</span></span>
          </div>
          <div className="bg-card rounded-xl border border-border/80 p-4 space-y-1 hover:border-primary/30 transition shadow-xs">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Liquidez Débito</span>
            <strong className="text-foreground text-sm block">Mercado Pago</strong>
            <span className="text-[11px] font-bold text-primary block">D+0 (Na hora)</span>
          </div>
          <div className="bg-card rounded-xl border border-border/80 p-4 space-y-1 hover:border-primary/30 transition shadow-xs">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Mais Vouchers VR/VA</span>
            <strong className="text-foreground text-sm block">PagBank / Stone</strong>
            <span className="text-[11px] font-bold text-primary block">Ampla Aceitação</span>
          </div>
          <div className="bg-card rounded-xl border border-border/80 p-4 space-y-1 hover:border-primary/30 transition shadow-xs col-span-2 md:col-span-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">Suporte Técnico PJ</span>
            <strong className="text-foreground text-sm block">Stone</strong>
            <span className="text-[11px] font-bold text-primary block">Nota ⭐ 9.1</span>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          * <strong>Aviso:</strong> Estas avaliações servem apenas como referência geral de mercado. A melhor operadora real depende exclusivamente do seu faturamento mensal e do tipo de venda (ex: faturar menos de R$ 2.000 inviabiliza o plano PromoTon da Ton). <strong>Sempre confirme a recomendação exata para a sua empresa utilizando o Simulador de Vendas.</strong>
        </p>
      </div>

      {/* Seletor de Marcas */}
      <div className="flex flex-wrap items-center justify-center gap-3 bg-muted/40 p-4 rounded-2xl border border-border">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mr-1">
          Selecionar operadoras:
        </span>
        {MAQUININHAS_DB.map((m) => {
          const isSelected = selectedIds.includes(m.id);
          return (
            <button
              key={m.id}
              onClick={() => toggleSelect(m.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold transition ${
                isSelected
                  ? "bg-primary border-primary text-primary-foreground shadow-sm"
                  : "bg-background border-border text-foreground hover:bg-muted"
              }`}
            >
              <span>{m.logo}</span>
              <span>{m.nome}</span>
              {isSelected ? (
                <span className="ml-1 text-[10px] bg-primary-foreground/20 px-1 rounded">Ativa</span>
              ) : (
                <span className="ml-1 text-[10px] text-muted-foreground">+ Add</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Grid de Comparação Desktop & Tablet (Esconde em Mobile menor) */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="min-w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="p-4 font-bold text-muted-foreground w-1/5 min-w-[200px]">Recurso / Detalhe</th>
              {selectedMaquinas.map((m) => (
                <th key={m.id} className="p-4 font-bold text-center border-l border-border w-1/5">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-2xl">{m.logo}</span>
                    <span className="text-foreground text-base">{m.nome}</span>
                    <span className="text-[10px] text-primary bg-primary/10 rounded-full px-2 py-0.5 mt-1">
                      Nota ⭐ {m.suporte.notaReclameAqui.toFixed(1)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Categoria: TAXAS */}
            <tr className="bg-muted/20 border-b border-border">
              <td colSpan={selectedMaquinas.length + 1} className="p-3 font-bold text-xs uppercase tracking-wider text-primary">
                Taxas & Tarifas de Venda
              </td>
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Débito</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border font-semibold text-foreground">
                  {formatPct(m.planos[0].taxas.debito)}
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Crédito à vista</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border font-semibold text-foreground">
                  {formatPct(m.planos[0].taxas.creditoVista)}
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Crédito 12x</td>
              {selectedMaquinas.map((m) => {
                let text = formatPct(m.planos[0].taxas.creditoParcelado[12]);
                if (m.id === "stone") text = "Customizável / Competitivo";
                if (m.id === "infinitepay") text = "Variável (progressiva)";
                return (
                  <td key={m.id} className="p-4 text-center border-l border-border font-semibold text-foreground">
                    {text}
                  </td>
                );
              })}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Pix</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border">
                  <span className="inline-block bg-success/15 text-success text-xs font-bold px-2 py-0.5 rounded-full">
                    {formatPct(m.planos[0].taxas.pix)}
                  </span>
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Prazo de Recebimento</td>
              {selectedMaquinas.map((m) => {
                let debitoText = `D+${m.planos[0].recebimentoPrazo.debito}`;
                let creditoText = `D+${m.planos[0].recebimentoPrazo.credito}`;
                if (m.id === "mercadopago") debitoText = "D+0 (Na hora)";
                return (
                  <td key={m.id} className="p-4 text-center border-l border-border text-xs leading-tight">
                    <div><strong>Débito:</strong> {debitoText}</div>
                    <div className="mt-1"><strong>Crédito:</strong> {creditoText}</div>
                  </td>
                );
              })}
            </tr>

            {/* Categoria: RECURSOS TECNOLÓGICOS */}
            <tr className="bg-muted/20 border-b border-border">
              <td colSpan={selectedMaquinas.length + 1} className="p-3 font-bold text-xs uppercase tracking-wider text-primary">
                Recursos & Tecnologia
              </td>
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Tap to Pay (NFC no celular)</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border">
                  {m.suportaTapToPay ? (
                    <span className="inline-flex items-center gap-1 text-success text-xs font-bold bg-success/10 px-2 py-1 rounded-full mx-auto">
                      <Check className="h-3.5 w-3.5" /> Sim
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground text-xs bg-muted px-2 py-1 rounded-full mx-auto">
                      <X className="h-3.5 w-3.5" /> Não
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Link de Pagamento</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border">
                  {m.suportaLinkPagamento ? (
                    <span className="inline-flex items-center gap-1 text-success text-xs font-bold bg-success/10 px-2 py-1 rounded-full mx-auto">
                      <Check className="h-3.5 w-3.5" /> Sim
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground text-xs bg-muted px-2 py-1 rounded-full mx-auto">
                      <X className="h-3.5 w-3.5" /> Não
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Conta Digital Integrada</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 border-l border-border text-center text-xs leading-relaxed max-w-[200px]">
                  {m.contaDigitalDetails}
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Aceita Vouchers (VR/VA)</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border">
                  {m.aceitaVouchers ? (
                    <span className="inline-flex items-center gap-1 text-success text-xs font-bold bg-success/10 px-2.5 py-1 rounded-full mx-auto">
                      <Check className="h-3.5 w-3.5" /> Sim
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-bold bg-amber-500/10 px-2.5 py-1 rounded-full mx-auto">
                      <X className="h-3.5 w-3.5" /> Não
                    </span>
                  )}
                </td>
              ))}
            </tr>

            {/* Categoria: ADESÃO E CUSTOS */}
            <tr className="bg-muted/20 border-b border-border">
              <td colSpan={selectedMaquinas.length + 1} className="p-3 font-bold text-xs uppercase tracking-wider text-primary">
                Adesão & Planos de Custo
              </td>
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Taxa de Aluguel</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border font-semibold">
                  {m.aluguelDisponivel && m.aluguelMensalidade ? (
                    <span className="text-foreground text-xs">{formatCurrency(m.aluguelMensalidade)}/mês</span>
                  ) : (
                    <span className="text-success text-xs font-bold bg-success/10 px-2 py-0.5 rounded-full">Sem Aluguel</span>
                  )}
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Valor de Compra (Leitor Principal)</td>
              {selectedMaquinas.map((m) => {
                const principal = m.maquininhasDisponiveis[0];
                return (
                  <td key={m.id} className="p-4 text-center border-l border-border text-xs leading-snug">
                    <div className="font-bold text-foreground">
                      {principal.precoAdesao > 0 ? formatCurrency(principal.precoAdesao) : "Comodato Grátis"}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Modelo: {principal.modelo}</div>
                  </td>
                );
              })}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Suporte a Comodato</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border text-xs">
                  {m.comodatoDisponivel ? (
                    <div>
                      <span className="text-success font-semibold block">Disponível</span>
                      {m.comodatoMetaFaturamento && (
                        <span className="text-[10px] text-muted-foreground">Faturamento &gt; {formatCurrency(m.comodatoMetaFaturamento)}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Não possui</span>
                  )}
                </td>
              ))}
            </tr>

            {/* Categoria: EDITORIAL */}
            <tr className="bg-muted/20 border-b border-border">
              <td colSpan={selectedMaquinas.length + 1} className="p-3 font-bold text-xs uppercase tracking-wider text-primary">
                Direcionamento & Recomendação
              </td>
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Público Altamente Indicado</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 border-l border-border max-w-[220px]">
                  <ul className="list-disc pl-4 space-y-1 text-xs text-muted-foreground">
                    {m.publicoIndicado.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Público Não Recomendado</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 border-l border-border max-w-[220px]">
                  <ul className="list-disc pl-4 space-y-1 text-xs text-muted-foreground">
                    {m.publicoNaoRecomendado.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>

            {/* Categoria: JORNADA / CTA CONTEXTUAL */}
            <tr className="bg-muted/20 border-b border-border">
              <td colSpan={selectedMaquinas.length + 1} className="p-3 font-bold text-xs uppercase tracking-wider text-primary">
                Jornada de Decisão
              </td>
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground flex items-center gap-1">
                Ação Recomendada
              </td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border">
                  <Link
                    to="/ferramentas/$slug"
                    params={{ slug: "simulador-maquininha" }}
                    onClick={() => trackEvent("jornada_navigated", {
                      origin_page: "/ferramentas/comparador-maquininhas",
                      destination_page: "/ferramentas/simulador-maquininha",
                      cta_text: "Simular com esta operadora →"
                    })}
                    className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary/10 hover:bg-primary/20 px-3 py-1.5 text-xs font-bold text-primary transition"
                  >
                    Simular com esta operadora →
                  </Link>
                </td>
              ))}
            </tr>

            {/* Categoria: AUDITORIA */}
            <tr className="bg-muted/20 border-b border-border">
              <td colSpan={selectedMaquinas.length + 1} className="p-3 font-bold text-xs uppercase tracking-wider text-primary">
                Auditoria de Dados
              </td>
            </tr>
            <tr className="border-b border-border hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Última Verificação</td>
              {selectedMaquinas.map((m) => {
                const dateParts = m.lastVerified.split("-");
                const formattedDate = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}` : m.lastVerified;
                return (
                  <td key={m.id} className="p-4 text-center border-l border-border text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {formattedDate}
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="hover:bg-muted/10 transition">
              <td className="p-4 font-medium text-foreground">Fonte de Informação</td>
              {selectedMaquinas.map((m) => (
                <td key={m.id} className="p-4 text-center border-l border-border text-xs">
                  <a
                    href={m.verificationSource}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    onClick={() => trackEvent("affiliate_clicked", {
                      operator_id: m.id,
                      source_page: "/ferramentas/comparador-maquininhas",
                      link_type: "site_oficial",
                      button_position: "desktop_table",
                      time_to_affiliate: getElapsedTime("time_to_affiliate")
                    })}
                    className="inline-flex items-center gap-1.5 text-primary hover:underline"
                  >
                    Site Oficial <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Visualização Mobile Stacking (Exibe apenas em celular) */}
      <div className="md:hidden space-y-8">
        {selectedMaquinas.map((m) => (
          <article 
            key={m.id} 
            className="rounded-2xl border border-border bg-card p-5 shadow-soft space-y-4"
          >
            {/* Cabeçalho do Card */}
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="text-3xl">{m.logo}</span>
                <div>
                  <h2 className="font-display font-bold text-lg text-foreground">{m.nome}</h2>
                  <span className="text-[10px] text-muted-foreground">Fonte Oficial Verificada</span>
                </div>
              </div>
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                ⭐ {m.suporte.notaReclameAqui.toFixed(1)}
              </span>
            </div>

            {/* Taxas Rápidas */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-muted/30 p-2.5 rounded-xl">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Débito</span>
                <span className="font-semibold text-foreground text-base">{formatPct(m.planos[0].taxas.debito)}</span>
              </div>
              <div className="bg-muted/30 p-2.5 rounded-xl">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Crédito à Vista</span>
                <span className="font-semibold text-foreground text-base">{formatPct(m.planos[0].taxas.creditoVista)}</span>
              </div>
              <div className="bg-muted/30 p-2.5 rounded-xl col-span-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Crédito 12x</span>
                <span className="font-semibold text-foreground">
                  {m.id === "stone" ? "Customizável" : m.id === "infinitepay" ? "Taxa Progressiva/Variável" : formatPct(m.planos[0].taxas.creditoParcelado[12])}
                </span>
              </div>
            </div>

            {/* Prazos de Recebimento */}
            <div className="text-xs space-y-1">
              <span className="font-bold text-muted-foreground block uppercase text-[10px] tracking-wider mb-1">Prazos</span>
              <div className="flex justify-between">
                <span>Débito:</span>
                <span className="font-semibold text-foreground">
                  {m.id === "mercadopago" ? "D+0 (Na hora)" : `D+${m.planos[0].recebimentoPrazo.debito}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Crédito:</span>
                <span className="font-semibold text-foreground">D+{m.planos[0].recebimentoPrazo.credito}</span>
              </div>
            </div>

            {/* Recursos Adicionais */}
            <div className="text-xs space-y-2 pt-2 border-t border-border">
              <span className="font-bold text-muted-foreground block uppercase text-[10px] tracking-wider mb-1">Recursos & Custos</span>
              <div className="flex justify-between">
                <span>Tap to Pay (NFC):</span>
                <span className="font-semibold text-foreground">{m.suportaTapToPay ? "✓ Sim" : "✗ Não"}</span>
              </div>
              <div className="flex justify-between">
                <span>Link de Pagamento:</span>
                <span className="font-semibold text-foreground">{m.suportaLinkPagamento ? "✓ Sim" : "✗ Não"}</span>
              </div>
              <div className="flex justify-between">
                <span>Aceita VR/VA:</span>
                <span className="font-semibold text-foreground">{m.aceitaVouchers ? "✓ Sim" : "✗ Não"}</span>
              </div>
              <div className="flex justify-between">
                <span>Aluguel:</span>
                <span className="font-semibold text-success">
                  {m.aluguelDisponivel && m.aluguelMensalidade ? formatCurrency(m.aluguelMensalidade) : "Sem custo mensal"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Adesão/Compra:</span>
                <span className="font-semibold text-foreground">
                  {m.maquininhasDisponiveis[0].precoAdesao > 0 ? formatCurrency(m.maquininhasDisponiveis[0].precoAdesao) : "Comodato Grátis"}
                </span>
              </div>
            </div>

            {/* Conta PJ */}
            <div className="bg-muted/40 p-3 rounded-xl text-xs leading-relaxed text-muted-foreground">
              <strong className="text-foreground block mb-0.5">Conta Digital PJ:</strong>
              {m.contaDigitalDetails}
            </div>

            {/* Indicações */}
            <div className="grid grid-cols-2 gap-4 text-xs pt-2 border-t border-border">
              <div>
                <strong className="text-success block mb-1">Ideal Para:</strong>
                <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                  {m.publicoIndicado.slice(0, 2).map((p, idx) => (
                    <li key={idx}>{p}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong className="text-amber-600 block mb-1">Não Indicado:</strong>
                <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                  {m.publicoNaoRecomendado.slice(0, 2).map((p, idx) => (
                    <li key={idx}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Verificação */}
            <div className="flex items-center justify-between text-[11px] text-muted-foreground bg-muted/20 p-2 rounded-lg">
              <span>Auditoria: {m.lastVerified.split("-").reverse().join("/")}</span>
              <a
                href={m.verificationSource}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => trackEvent("affiliate_clicked", {
                  operator_id: m.id,
                  source_page: "/ferramentas/comparador-maquininhas",
                  link_type: "site_oficial",
                  button_position: "mobile_card",
                  time_to_affiliate: getElapsedTime("time_to_affiliate")
                })}
                className="inline-flex items-center gap-0.5 text-primary font-semibold hover:underline"
              >
                Site Oficial <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>

            {/* CTA Contextual Mobile */}
            <div className="pt-2">
              <Link
                to="/ferramentas/$slug"
                params={{ slug: "simulador-maquininha" }}
                onClick={() => trackEvent("jornada_navigated", {
                  origin_page: "/ferramentas/comparador-maquininhas",
                  destination_page: "/ferramentas/simulador-maquininha",
                  cta_text: "Simular com esta operadora →"
                })}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary px-3 py-2.5 text-xs font-bold text-primary hover:bg-primary/5 transition"
              >
                <Calculator className="h-3.5 w-3.5" /> Simular com esta operadora →
              </Link>
            </div>
          </article>
        ))}
      </div>

      {/* CTA para o Simulador de Maquininhas */}
      <section className="max-w-4xl mx-auto">
        <div className="rounded-3xl border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex-1 text-center md:text-left space-y-2">
            <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
              <Calculator className="h-3.5 w-3.5" /> Ferramenta integrada
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground font-display">
              Quer saber quanto vai sobrar de verdade no bolso?
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              O Comparador compara os recursos gerais. Use nosso simulador matemático de vendas 
              para digitar o valor exato da sua venda PJ ou CPF e calcular o retorno líquido de cada marca!
            </p>
          </div>
          <Link
            to="/ferramentas/$slug"
            params={{ slug: "simulador-maquininha" }}
            onClick={() => trackEvent("jornada_navigated", {
              origin_page: "/ferramentas/comparador-maquininhas",
              destination_page: "/ferramentas/simulador-maquininha",
              cta_text: "Simular Vendas Líquidas"
            })}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:opacity-90 px-6 py-3 text-sm font-bold text-primary-foreground transition shrink-0 shadow-lg shadow-primary/20"
          >
            Simular Vendas Líquidas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Aviso de Responsabilidade Legal / Transparência */}
      <footer className="max-w-3xl mx-auto text-center text-xs text-muted-foreground leading-relaxed space-y-2">
        <p className="flex items-center justify-center gap-1 text-amber-600 font-semibold">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Aviso Importante de Isenção de Responsabilidade
        </p>
        <p>
          As taxas e condições comerciais das operadoras de maquininhas são coletadas a partir de seus sites 
          oficiais e regulamentos públicos. No entanto, as operadoras podem alterar taxas, juros e prazos de 
          recebimento a qualquer momento sem aviso prévio. Este comparador é um guia gratuito de referência 
          e não substitui a consulta dos termos vigentes nos sites oficiais de cada empresa antes de qualquer contratação.
        </p>
        <p>
          O MEI Toolbox atua de forma independente e isenta. A exibição de links oficiais de auditoria tem como 
          único objetivo assegurar a transparência de informações ao empreendedor.
        </p>
      </footer>
    </section>
  );
}
